import type { JSX } from "preact";
import { Auth } from "@gss/common/Auth";
import { Button } from "@gss/components/Button";
import { MenuLoginButton } from "@gss/components/MenuLoginButton";
import { getStodstrukturenUrl } from "@gss/components/MenuNodes";
import { Navbar, NavbarBrand, NavbarNav } from "@gss/components/Navbar";
import { NavItem } from "@gss/components/NavItem";
import { NavLink } from "@gss/components/NavLink";
import { Popover } from "@gss/components/Popover";
import { useTranslation } from "../util/Translation";
import { Flag } from "./Flag";
import npeLogo from "./npeLogo.svg";

export function Menu(): JSX.Element {
    const { translation } = useTranslation();

    return (
        <Navbar
            brand={
                <NavbarBrand href="https://patientenkat.se">
                    <img src={npeLogo} alt="Nationell Patientenkät" />
                </NavbarBrand>
            }
        >
            <NavbarNav className="me-auto">
                {Auth.isStodstrukturenUser() && (
                    <NavItem>
                        <NavLink
                            className="external-menu-link"
                            absolute
                            href={getStodstrukturenUrl()}
                        >
                            Stödstrukturen
                        </NavLink>
                    </NavItem>
                )}
            </NavbarNav>

            <NavbarNav>
                <Popover
                    placement="bottom"
                    overlay={(hide) => <LanguageSelection hide={hide} />}
                >
                    <Button
                        variant="light"
                        aria-label={translation.menu.changeLanguage}
                        title={translation.menu.changeLanguage}
                    >
                        <Flag
                            language={translation.code}
                            className="language-selection-flag"
                            aria-hidden
                        />
                    </Button>
                </Popover>

                <MenuLoginButton translation={translation} />
            </NavbarNav>
        </Navbar>
    );
}

/* LanguageSelection */

interface Props {
    hide?: () => void;
}

function LanguageSelection({ hide }: Props): JSX.Element {
    const { translation, languages, changeLanguage } = useTranslation();
    return (
        <div className="p-2">
            {languages.map((lang, i) => {
                const text = translation.menu.changeLanguageTo(lang.name);
                return (
                    <Button
                        key={lang.code}
                        variant="light"
                        className={i !== 0 ? "ms-2" : undefined}
                        autoFocus={i === 0}
                        aria-label={text}
                        title={text}
                        onClick={() => {
                            hide?.();
                            if (translation.code !== lang.code) {
                                changeLanguage(lang.code);
                            }
                        }}
                    >
                        <Flag
                            language={lang.code}
                            className="language-selection-flag"
                        />
                    </Button>
                );
            })}
        </div>
    );
}
